import {
  Button,
  FlexBox,
  Page,
  Spinner,
  Tooltip,
} from '@dixa/lib-shared-habla';
import { client as elevioClient } from 'elevio';
import { isQueryError, isResponseWithData } from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';
import type {
  CardType,
  ContentType,
  CustomCardIntegration,
  CustomCardRequestIntegration,
  HttpMethod,
} from 'lib-shared-types-api';
import { useCallback, useEffect, useRef } from 'react';
import { SubmitHandler, useForm, useFormContext } from 'react-hook-form';

import { integrationsApi } from 'apis/integrations';
import { appActions } from 'app/app.actions';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { articles } from 'utils/elevio';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useAppPathParams } from 'utils/url/useAppPathParams';

import { SettingsIntegrationsDocumentationButton } from '../../../../../../components/documentationButton/documentationButton';
import { cardV2 } from '../../../../cards.types';
import { CardsTestResult } from '../../../components/cardsTestResult/cardsTestResult';
import { OAuthFormData } from '../../../components/oAuth';
import { isResponseWithErrors } from '../../../utils/validateMessages';
import { CustomCardsForm } from '../../components/form/settingsIntegrationsCardsCustomForm';
import { getAuthorizationHeaderType } from '../../utils/getAuthorizationType/getAuthorizationType';

type EditCustomCardFormValues = {
  authorizationHeader?: string;
  customHeaders?: Dictionary<string>;
  oAuthStrategy?: string;
} & Omit<CustomCardIntegration, 'request'> & {
    request: {
      http_method: HttpMethod[];
      content_type: ContentType[];
    } & Omit<CustomCardRequestIntegration, 'http_method' | 'content_type'>;
  };

/** Edit custom cards page. */
export const SettingsIntegrationsCardsCustomEditView = (): JSX.Element => {
  const { id } = useAppPathParams(
    '/settings/integrations/custom-card/edit/:id',
  );

  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const notify = useNotifications();
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const { control, handleSubmit, reset, getValues } =
    useForm<EditCustomCardFormValues>({
      mode: 'onTouched',
      defaultValues: {
        request: {
          http_method: ['GET'],
          content_type: [],
          body: '',
        },
      },
    });

  const {
    formState: { isDirty: isOAuthFormDirty },
  } = useFormContext<OAuthFormData>();

  const disableCustomCardSaveButton = isOAuthFormDirty;

  const [getCustomCardById, { data, isLoading }] =
    integrationsApi.useLazyReadCustomCardIntegrationByIdQuery();

  const [updateCustomCard] =
    integrationsApi.useUpdateCustomCardIntegrationMutation();

  const [deleteCustomCard] =
    integrationsApi.useDeleteCustomCardIntegrationMutation();

  const [
    updateCustomCardTest,
    { data: testResult, isLoading: isTestLoading, isError: hasTestFailed },
  ] = integrationsApi.useUpdateCustomCardTestIntegrationMutation();

  useEffect(() => {
    if (id) {
      getCustomCardById({ hookId: id });
    }
  }, [id]);

  // We need to reset the form values when the data is available, since the
  // custom card is fetched async (instead of using defaultValues).
  useEffect(() => {
    if (data?.data) {
      const customCard = data.data;
      const headers = customCard?.request?.headers;

      const authenticationKey =
        headers &&
        Object.keys(headers).find(
          (key) => key.toLowerCase() === 'authorization',
        );

      reset({
        id: customCard.id,
        title: customCard.title,
        created_at: customCard.created_at,
        card_version: customCard.card_version,
        integration_type: customCard.integration_type,
        template: customCard.template,
        request: {
          ...customCard.request,
          uri_template: customCard?.request?.uri_template,
          headers: customCard?.request?.headers ?? {},
          content_type: customCard?.request?.content_type
            ? [customCard.request.content_type]
            : undefined,
          http_method: customCard?.request?.http_method
            ? [customCard.request.http_method]
            : ['GET' as HttpMethod],
          o_auth_connection_id: customCard?.request?.o_auth_connection_id,
        },
        ...(authenticationKey && {
          authorizationHeader: headers[authenticationKey],
        }),
        ...(headers &&
          Object.keys(headers).length > 0 && {
            customHeaders: Object.keys(headers)
              .filter((key) => key !== authenticationKey)
              .reduce((obj, key) => {
                obj[key] = headers[key];
                return obj;
              }, {}),
          }),
      });
    }
  }, [data, reset]);

  const onSubmit: SubmitHandler<EditCustomCardFormValues> = async (
    formValues,
  ) => {
    const authorizationHeader = formValues?.authorizationHeader;
    const customHeaders = formValues?.customHeaders;
    const uriTemplate = formValues?.request?.uri_template;
    const hasUriTemplate = !!uriTemplate && uriTemplate !== '';

    const response = await updateCustomCard({
      id: formValues.id,
      created_at: formValues.created_at,
      title: formValues.title,
      template: formValues.template,
      card_version: cardV2,
      integration_type: 'custom' as CardType,
      ...(hasUriTemplate && {
        request: {
          headers: {
            ...(customHeaders &&
              Object.keys(customHeaders).length > 0 && {
                ...customHeaders,
              }),
            ...(authorizationHeader && { authorization: authorizationHeader }),
          },
          uri_template: uriTemplate,
          http_method: formValues.request.http_method[0],
          content_type: formValues?.request?.content_type?.[0] || undefined,
          body: !!formValues.request?.body
            ? formValues.request.body
            : undefined,
          o_auth_connection_id: formValues?.request?.o_auth_connection_id,
        },
      }),
    });

    if (isResponseWithData(response)) {
      notify(
        {
          title: 'Success',
          text: 'Custom card has been updated.',
          icon: 'i-done',
          time: 5000,
        },
        NotificationType.Success,
      );

      if (hasUriTemplate) {
        const authorizationHeaderType =
          getAuthorizationHeaderType(authorizationHeader) ||
          (formValues?.request?.o_auth_connection_id && 'oauth') ||
          undefined;

        ampli.settingsIntegrationsCustomCardEdited({
          customHeaders: customHeaders ? Object.keys(customHeaders).length : 0,
          authorizationHeader: authorizationHeaderType,
          oAuthStrategy: formValues?.oAuthStrategy,
          httpMethod: formValues.request.http_method[0],
          contentType: formValues?.request?.content_type?.[0] || undefined,
          body: !!formValues.request?.body,
        });
      } else {
        ampli.settingsIntegrationsCustomCardEdited();
      }

      const { data } = response;

      navigate({
        path: '/settings/integrations/custom-card/edit/:id',
        params: { path: { id: data.data.id } },
      });
    } else {
      const errorMessage =
        isQueryError(response.error) &&
        isResponseWithErrors(response.error.data)
          ? response.error.data.errors.join(' ')
          : null;

      notify(
        {
          title: 'Error while updating the custom card.',
          text: errorMessage,
        },
        NotificationType.Error,
      );
    }
  };

  const handleTest = useCallback(
    async (formValues) => {
      await updateCustomCardTest({ hookId: id, template: formValues.template });

      setTimeout(() => {
        if (scrollRef?.current) {
          scrollRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }, 300);

      ampli.settingsIntegrationsCardEditTest({
        cardType: 'custom',
        cardId: id,
      });
    },
    [id, updateCustomCardTest],
  );

  const handleDeleteCustomCard = async (customCard: CustomCardIntegration) => {
    const { isConfirmed } = await dispatch(
      appActions.showModal({
        modal: {
          type: 'app:confirm',
          arg: {
            title: `Delete "${customCard.title}"`,
            description: 'Are you sure you want to delete this card?',
            confirm: {
              label: 'Delete',
            },
          },
        },
      }),
    );

    if (isConfirmed) {
      const name = customCard.title;
      try {
        await deleteCustomCard({ hookId: customCard.id });

        notify(
          {
            title: 'Success',
            text: `"${name}" has been deleted.`,
            icon: 'i-done',
            time: 5000,
          },
          NotificationType.Success,
        );

        navigate({ path: '/settings/integrations' });
      } catch (error) {
        notify(
          { title: `Error while deleting "${name}".` },
          NotificationType.Error,
        );
      }
    }
  };

  return (
    <Page.Section
      title="Edit custom card"
      titleMeta={
        <SettingsIntegrationsDocumentationButton
          onClick={() => {
            ampli.settingsIntegrationsCardReadDocumentation({
              cardRoute: 'edit',
              cardType: 'custom',
            });

            elevioClient.openArticle(articles.settings.cards.custom);
          }}
        />
      }
      controls={
        <>
          <Button
            icon="play"
            variant="secondary"
            onClick={() =>
              navigate({
                path: '/settings/integrations/custom-card/test/:id',
                params: { path: { id } },
              })
            }
            isLoading={isTestLoading}
          >
            Test with parameters
          </Button>

          <Button
            icon="trash"
            variant="secondary"
            disabled={disableCustomCardSaveButton}
            onClick={() =>
              handleDeleteCustomCard(data?.data as CustomCardIntegration)
            }
          >
            Delete card
          </Button>

          <Tooltip
            content={
              disableCustomCardSaveButton
                ? 'The OAuth configuration needs to be saved before the card is saved'
                : undefined
            }
          >
            <Button
              disabled={disableCustomCardSaveButton}
              onClick={handleSubmit(onSubmit)}
            >
              Save changes
            </Button>
          </Tooltip>
        </>
      }
    >
      {isLoading ? (
        <Spinner />
      ) : (
        <>
          <CustomCardsForm<EditCustomCardFormValues>
            control={control}
            reset={reset}
            getValues={getValues}
            handleCustomCardSubmit={handleSubmit(onSubmit)}
          />

          <FlexBox margin="300 0">
            <Button
              icon="play"
              variant="primary"
              onClick={handleSubmit(handleTest)}
              isLoading={isTestLoading}
            >
              Test the template
            </Button>
          </FlexBox>

          <CardsTestResult
            card={data?.data}
            testResult={testResult?.data}
            scrollRef={scrollRef}
            isLoading={isTestLoading}
            hasFailed={hasTestFailed}
          />
        </>
      )}
    </Page.Section>
  );
};
